/**
 * Name coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring_themes`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const nameColoringPagesPack: ToilePack = {
    slug: 'name-coloring-pages',
    category: 'coloring',
    name: 'Name coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'bubble', label: 'Bubble letters', subject: 'the name "EMMA" in fat bubble letters across the page, stars, hearts and swirls scattered around'},
        {key: 'animals', label: 'Name with animals', subject: 'the name "ARJUN" in tall block letters with a lion, a giraffe and a monkey peeking from behind the letters'},
        {key: 'pattern', label: 'Patterned letters', subject: 'the name "SARA" in large outline letters, each letter filled with a different doodle pattern of flowers, dots and stripes'},
    ],
    styles: [
        {key: 'bold', label: 'Big and simple', style: 'thick black outline, very large open letters and a few big doodles, made for toddlers', palette: 'black ink only', framing: 'The name across the page with a wide white margin'},
        {key: 'playful', label: 'Playful', style: 'medium black outline, cartoon doodles, small stars and swirls around and inside the letters', palette: 'black ink only', framing: 'The name across the page with a white margin'},
        {key: 'intricate', label: 'Intricate', style: 'thin black line, mandala and zentangle fills inside the letters, for teens', palette: 'black ink only', framing: 'The name across the page with a thin white margin'},
    ],
    directions: ['dinosaurs', 'unicorns', 'space'],
    directionHint: 'try: dinosaurs',
    seo: {
        title: 'Name coloring pages: bubble letters and doodles',
        metaDescription: "Nine name coloring pages that spell a child's name in big outline letters, with animals, doodles or patterns inside, in three styles.",
        h1: 'Name coloring pages: bubble letters, name with animals, patterned letters',
        tagline: 'A name spelled in big outline letters, filled with doodles, animals or patterns.',
        intro: 'Name coloring pages for bedroom doors, classroom welcome walls and birthday gifts. The pages spell a name in big outline letters: bubble letters with doodles, a name with animals in the letters and letters filled with patterns. The styles are big and simple, playful and intricate. Type a theme, such as dinosaurs or space, and the same style carries it.',
        useCases: [
            "a name sign for a child's bedroom door",
            'a welcome page for a classroom wall',
            'a birthday gift page to color',
            'a patterned page for a teen or adult',
        ],
        faq: [
            {q: 'What is the difference between the styles?', a: 'Big and simple uses thick outlines and wide letters with large simple decorations. Playful adds cartoon doodles, stars and swirls, and intricate fills the letters with mandala and zentangle patterns.'},
            {q: 'What are the three kinds of letters?', a: 'Bubble letters with doodles, a name with animals in the letters, and patterned letters with a pattern inside each one.'},
            {q: 'Can I ask for a different theme?', a: 'Yes. A theme you type, such as dinosaurs, unicorns or space, is a request; the page keeps the same style but may not match it exactly.'},
            {q: 'Are the pages in color?', a: 'No. Each page is a black line drawing on white paper, ready to print and color.'},
        ],
    },
}
